'use client';

import { useRef, useState } from 'react';

import { type FilmId, FILMS } from '../_content/films';

/**
 * A rendered marketing film. Opens on a branded poster button (the live film
 * player's play disc); the first click starts playback and hands over to the
 * browser's native controls, which own keyboard, captions, and fullscreen.
 *
 * Styles live in _styles/marketing-film.css, scoped to
 * `.marketing-page[data-theme='light']`: the host page must import that file
 * (and marketing-home.css, for the tokens) and render inside that wrapper.
 */
export function MarketingFilm({ id }: { id: FilmId }) {
  const film = FILMS[id];
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const minutes = Math.max(1, Math.round(film.durationSeconds / 60));

  function start() {
    setStarted(true);
    const video = videoRef.current;
    if (!video) return;
    video.focus();
    // A rejected play() (autoplay policy, unsupported codec) is the viewer's
    // environment, not a defect: native controls are already showing, so they
    // can press play themselves. Nothing to report.
    video.play().catch(() => undefined);
  }

  return (
    <figure className="marketing-film">
      <video
        ref={videoRef}
        controls={started}
        preload="none"
        playsInline
        poster={film.poster}
        aria-label={film.title}
        tabIndex={started ? 0 : -1}
      >
        <source src={film.src} type="video/mp4" />
        <track kind="captions" src={film.captions} srcLang="en" label="English" default />
      </video>
      {!started && (
        <button
          type="button"
          className="marketing-film-play"
          data-attr="marketing-film-play"
          onClick={start}
        >
          <span className="marketing-film-disc" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path
                d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className="marketing-film-label">
            Watch the film <span aria-hidden="true">·</span> {minutes} min
          </span>
        </button>
      )}
    </figure>
  );
}
